import { win, loading, appSettings } from '../context'
import imagesLoaded from 'imagesloaded'
import type { Element as DOMElement } from 'domhandler'

const getViewportSize = (): { width: number; height: number } => {
  const viewport = window.visualViewport

  return {
    width: viewport?.width ?? window.innerWidth ?? document.documentElement.clientWidth,
    height: viewport?.height ?? window.innerHeight ?? document.documentElement.clientHeight,
  }
}

// Window width / height
const setWin = (): void => {
  const { width, height } = getViewportSize()

  win.w = width
  win.h = height
}

// Is Loading
const isLoading = (): boolean => loading.value

// Set loading
const setLoading = (newVal: boolean): void => {
  loading.value = newVal
  if (isLoading()) document.documentElement.setAttribute('data-loading', 'loading')
  else document.documentElement.removeAttribute('data-loading')
}

// Set scroller
const setScroller = (newElement: Element | null): void => {
  appSettings.scroller = newElement
}

// Set Vh
const setVh = (): void => {
  document.documentElement.style.setProperty('--vh', win.h / 100 + 'px');
}

// Set header height
const setHeaderHeight = (): void => {
  const siteHeader = document.querySelector('.hg-header') as HTMLElement
  if (siteHeader) {
    const inner = siteHeader.querySelector<HTMLElement>('.hg-header__inner')
    const nav = siteHeader.querySelector<HTMLElement>('.hg-nav')
    const fullHeight = siteHeader.offsetHeight
    const innerHeight = inner?.offsetHeight || 0
    const navHeight = nav?.offsetHeight || Math.max(fullHeight - innerHeight, 0)

    document.documentElement.style.setProperty('--header-full-height', fullHeight + 'px')
    document.documentElement.style.setProperty('--header-inner-height', innerHeight + 'px')
    document.documentElement.style.setProperty('--header-nav-height', navHeight + 'px')
    document.documentElement.style.setProperty('--header-height', fullHeight + 'px')
  }
}

// Is touch device
const isTouchDevice: boolean = "ontouchstart" in document.documentElement;

// Async delay
const delay = (ms = 0): Promise<void> => {
  return new Promise((resolve)=>{
    setTimeout(resolve, ms)
  })
}

// Preload images
const preloadImages = (container: Document | Element = document, selector = 'img'): Promise<void> | undefined => {
  const images = container.querySelectorAll(selector)
  if (!images) return
  return new Promise((resolve) => {
    imagesLoaded(images as any, { background: true }, resolve)
  })
}

// Element is in viewport
const isInViewport = (element: Element): boolean => {
  const rect = (element as HTMLElement).getBoundingClientRect()
  
  return  rect.bottom > 0 &&
      rect.right > 0 &&
      rect.left < (win.w || document.documentElement.clientWidth) &&
      rect.top < (win.h || document.documentElement.clientHeight)
}

// Get percent
const getPercent = (current: number, total: number): number => {
  return (current * 100) / total
}

// Set iframe aspect ratio
const setIframeAspectRatio = (wrapper: HTMLElement, iframe: HTMLIFrameElement): void => {
  const width   = iframe.getAttribute('width') || ''
  const height  = iframe.getAttribute('height') || ''
  wrapper.style.aspectRatio = width + ' / ' + height
}

// Wrap element
const wrap = (el: Element | null, wrapper: HTMLElement): void => {
  if (el && el.parentNode) {
    el.parentNode.insertBefore(wrapper, el)
    wrapper.appendChild(el)
  }
}

// Unwrap element
const unwrap = (el: Element | null): void => {
  if (!el) return
  const wrapper = el.parentElement
  if (!wrapper) return
  const parent = wrapper.parentElement
  if (!parent) return
  while (wrapper.firstChild) {
    parent.insertBefore(wrapper.firstChild, wrapper)
  }
  parent.removeChild(wrapper)
}

// Leading zero numbers
const leadingZero = (num: number | string, size: number): string => {
  let s = num.toString()
  while (s.length < size) s = "0" + s
  return s
}

// Preload videos: asigna `data-src` → `src`, espera a que cada <video> tenga
// datos (o falle) y luego invoca `play()` en los que llevan la clase `autoplay`.
const preloadVideos = (
	container: Document | Element = document,
	selector = 'video',
): Promise<void> => {
	const videos = Array.from(
		container.querySelectorAll<HTMLVideoElement>(selector),
	)
	const n = videos.length

	const playAutoplay = () => {
		container.querySelectorAll<HTMLVideoElement>('video.autoplay').forEach((v) => {
			void v.play().catch(() => {
				// Política de autoplay (p. ej. sin interacción del usuario)
			})
		})
	}

	return new Promise((resolve) => {
		if (n === 0) {
			resolve()
			return
		}

		let done = 0
		const mark = () => {
			done++
			if (done === n) {
				playAutoplay()
				resolve()
			}
		}

		videos.forEach((video) => {
			if (video.dataset.src) {
				video.src = video.dataset.src
			}

			if (!video.getAttribute('src') && !video.currentSrc) {
				mark()
				return
			}

			if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
				video.autoplay = true
			}

			if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
				mark()
				return
			}

			video.addEventListener('loadeddata', mark, { once: true })
			video.addEventListener(
				'error',
				(e) => {
					console.error('Error loading video:', e)
					mark()
				},
				{ once: true },
			)
		})
	})
}

export {
  win,
  setWin,
  setScroller,
  setLoading,
  isLoading,
  setVh,
  setHeaderHeight,
  isTouchDevice,
  preloadImages,
  isInViewport,
  delay,
  getPercent,
  setIframeAspectRatio,
  wrap,
  unwrap,
  leadingZero,
  preloadVideos,
}

